import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckCircleIcon, CloudArrowUpIcon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useFetcher } from '@remix-run/react';
import { useEffect, useRef, useState } from 'react';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { type CoreTheme } from '#speaker-shop/hooks/useCoreTheme.ts';

interface BookingResponse {
    success: boolean;
    errors?: Record<string, string[]>;
    error?: string;
}

const countries = [ 'DE', 'AT', 'CH', 'LI', 'LU', 'FR', 'IT', 'NL', 'BE', 'GB', 'US', 'OTHER' ] as const;
const media = [ 'podcast', 'youtube', 'commercial', 'audiobook', 'elearning', 'imagefilm', 'social', 'other' ] as const;
const inputClass = 'core-input w-full rounded-md px-4 py-3 outline-none transition-colors';

export function NaturalVoiceBookingDialog({ speakerName, theme, open, onClose }: {
    speakerName: string;
    theme: CoreTheme;
    open: boolean;
    onClose: () => void;
}) {
    const { rawT } = useEditableTranslation('core-shop');
    const fetcher = useFetcher<BookingResponse>();
    const formRef = useRef<HTMLFormElement>(null);
    const [ result, setResult ] = useState<BookingResponse | null>(null);
    const [ fileName, setFileName ] = useState<string | null>(null);
    const isSubmitting = fetcher.state !== 'idle';

    useEffect(() => {
        if (!open) {
            return;
        }
        formRef.current?.reset();
        setFileName(null);
        setResult(null);
    }, [ open ]);

    useEffect(() => {
        if (!fetcher.data) {
            return;
        }
        setResult(fetcher.data);
        if (!fetcher.data.success) {
            return;
        }
        const timeout = window.setTimeout(onClose, 3000);
        return () => window.clearTimeout(timeout);
    }, [ fetcher.data, onClose ]);

    const errorFor = (field: string) => result?.errors?.[field]?.[0];

    return (
        <Dialog open={open} onClose={onClose} className='core-booking-theme relative z-400' data-theme={theme}>
            <div className='fixed inset-0 bg-black/65 backdrop-blur-sm' aria-hidden='true' />
            <div className='core-booking-viewport fixed inset-0 overflow-y-auto p-0 sm:p-6'>
                <div className='core-booking-align flex min-h-full items-center justify-center'>
                    <DialogPanel className='core-booking-panel relative w-full max-w-2xl overflow-hidden'>
                        <button
                            type='button'
                            className='core-dialog-close absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full opacity-60 transition hover:bg-[var(--core-soft-bg)] hover:opacity-100'
                            onClick={onClose}
                            aria-label={rawT('natural-booking.close')}
                        >
                            <XMarkIcon className='size-6' />
                        </button>

                        {result?.success
                            ? (
                                <div className='core-booking-success px-6 py-16 text-center sm:px-8'>
                                    <CheckCircleIcon className='mx-auto mb-4 size-20 text-green-500' />
                                    <DialogTitle className='mb-2 text-2xl font-bold'>
                                        {rawT('natural-booking.success-title')}
                                    </DialogTitle>
                                    <p className='core-booking-muted'>{rawT('natural-booking.success-message')}</p>
                                </div>
                            )
                            : (
                                <div className='core-booking-scroll max-h-[100dvh] overflow-y-auto p-6 sm:max-h-[90vh] sm:p-8'>
                                    <div className='mb-6 pr-10'>
                                        <DialogTitle className='mb-2 text-2xl font-bold sm:text-3xl'>
                                            {rawT('natural-booking.title')}
                                        </DialogTitle>
                                        <p className='core-booking-muted'>
                                            {rawT('natural-booking.subtitle', { speaker: speakerName })}
                                        </p>
                                    </div>

                                    <fetcher.Form
                                        ref={formRef}
                                        method='post'
                                        action='/resources/natural-voice-booking'
                                        encType='multipart/form-data'
                                        className='space-y-5'
                                        onSubmit={() => setResult(null)}
                                    >
                                        <HoneypotInputs />
                                        <div className='core-booking-speaker rounded-md p-4'>
                                            <span className='mb-1 block text-sm font-bold text-[var(--core-blue)]'>
                                                {rawT('natural-booking.selected-speaker')}
                                            </span>
                                            <strong className='text-lg'>{speakerName}</strong>
                                        </div>

                                        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                                            <Field
                                                label={rawT('natural-booking.name')}
                                                error={errorFor('name')}
                                                required
                                            >
                                                <input
                                                    className={inputClass}
                                                    name='name'
                                                    required
                                                    maxLength={100}
                                                    placeholder={rawT('natural-booking.name-placeholder')}
                                                />
                                            </Field>
                                            <Field
                                                label={rawT('natural-booking.email')}
                                                error={errorFor('email')}
                                                required
                                            >
                                                <input
                                                    className={inputClass}
                                                    type='email'
                                                    name='email'
                                                    required
                                                    placeholder={rawT('natural-booking.email-placeholder')}
                                                />
                                            </Field>
                                        </div>

                                        <Field label={rawT('natural-booking.company')} error={errorFor('company')}>
                                            <input
                                                className={inputClass}
                                                name='company'
                                                maxLength={150}
                                                placeholder={rawT('natural-booking.company-placeholder')}
                                            />
                                        </Field>

                                        <div className='grid grid-cols-3 gap-4'>
                                            <div className='col-span-2'>
                                                <Field
                                                    label={rawT('natural-booking.street')}
                                                    error={errorFor('street')}
                                                    required
                                                >
                                                    <input
                                                        className={inputClass}
                                                        name='street'
                                                        required
                                                        maxLength={150}
                                                        placeholder={rawT('natural-booking.street-placeholder')}
                                                    />
                                                </Field>
                                            </div>
                                            <Field
                                                label={rawT('natural-booking.street-nr')}
                                                error={errorFor('streetNr')}
                                                required
                                            >
                                                <input
                                                    className={inputClass}
                                                    name='streetNr'
                                                    required
                                                    maxLength={20}
                                                    placeholder={rawT('natural-booking.street-nr-placeholder')}
                                                />
                                            </Field>
                                        </div>

                                        <div className='grid grid-cols-3 gap-4'>
                                            <Field label={rawT('natural-booking.zip')} error={errorFor('zip')} required>
                                                <input
                                                    className={inputClass}
                                                    name='zip'
                                                    required
                                                    maxLength={20}
                                                    placeholder={rawT('natural-booking.zip-placeholder')}
                                                />
                                            </Field>
                                            <div className='col-span-2'>
                                                <Field
                                                    label={rawT('natural-booking.city')}
                                                    error={errorFor('city')}
                                                    required
                                                >
                                                    <input
                                                        className={inputClass}
                                                        name='city'
                                                        required
                                                        maxLength={100}
                                                        placeholder={rawT('natural-booking.city-placeholder')}
                                                    />
                                                </Field>
                                            </div>
                                        </div>

                                        <Field
                                            label={rawT('natural-booking.country')}
                                            error={errorFor('country')}
                                            required
                                        >
                                            <select className={inputClass} name='country' required defaultValue=''>
                                                <option value='' disabled>
                                                    {rawT('natural-booking.select-placeholder')}
                                                </option>
                                                {countries.map(country => (
                                                    <option value={country} key={country}>
                                                        {rawT(`natural-booking.countries.${country}`)}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>

                                        <Field
                                            label={rawT('natural-booking.medium')}
                                            error={errorFor('medium')}
                                            required
                                        >
                                            <select className={inputClass} name='medium' required defaultValue=''>
                                                <option value='' disabled>
                                                    {rawT('natural-booking.select-placeholder')}
                                                </option>
                                                {media.map(medium => (
                                                    <option value={medium} key={medium}>
                                                        {rawT(`natural-booking.media.${medium}`)}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>

                                        <div>
                                            <span className='mb-2 block text-sm font-bold'>
                                                {rawT('natural-booking.file')}
                                            </span>
                                            <div className='core-booking-upload rounded-md p-6 text-center transition'>
                                                <input
                                                    id='natural-voice-text-file'
                                                    type='file'
                                                    name='textFile'
                                                    className='sr-only'
                                                    accept='.txt,.doc,.docx,.pdf'
                                                    onChange={event =>
                                                        setFileName(event.currentTarget.files?.[0]?.name ?? null)}
                                                />
                                                {fileName
                                                    ? (
                                                        <div className='flex items-center justify-center gap-3'>
                                                            <CheckCircleIcon className='size-7 text-green-600' />
                                                            <div>
                                                                <div className='font-bold'>{fileName}</div>
                                                                <button
                                                                    type='button'
                                                                    className='mt-1 text-xs font-medium text-red-600 hover:text-red-700'
                                                                    onClick={() => {
                                                                        const input = formRef.current?.elements
                                                                            .namedItem('textFile') as
                                                                                | HTMLInputElement
                                                                                | null;
                                                                        if (input) {
                                                                            input.value = '';
                                                                        }
                                                                        setFileName(null);
                                                                    }}
                                                                >
                                                                    {rawT('natural-booking.file-remove')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )
                                                    : (
                                                        <>
                                                            <CloudArrowUpIcon className='core-booking-muted mx-auto mb-3 size-10' />
                                                            <label
                                                                htmlFor='natural-voice-text-file'
                                                                className='block cursor-pointer font-bold text-[var(--core-blue)] hover:underline'
                                                            >
                                                                {rawT('natural-booking.file-select')}
                                                            </label>
                                                            <p className='core-booking-muted mt-2 text-xs'>
                                                                {rawT('natural-booking.file-hint')}
                                                            </p>
                                                        </>
                                                    )}
                                            </div>
                                            {errorFor('textFile') && (
                                                <span className='mt-1 block text-xs text-[var(--core-error,#dc2626)]'>
                                                    {errorFor('textFile')}
                                                </span>
                                            )}
                                        </div>

                                        <Field
                                            label={rawT('natural-booking.message')}
                                            error={errorFor('message')}
                                            required
                                        >
                                            <textarea
                                                className={`${inputClass} resize-none`}
                                                name='message'
                                                required
                                                minLength={10}
                                                maxLength={2000}
                                                rows={4}
                                                placeholder={rawT('natural-booking.message-placeholder')}
                                            />
                                        </Field>

                                        {result?.error && (
                                            <p className='text-sm font-bold text-[var(--core-error,#dc2626)]'>
                                                {result.error}
                                            </p>
                                        )}
                                        <button
                                            type='submit'
                                            disabled={isSubmitting}
                                            className='core-primary-button w-full px-8 py-4 text-lg disabled:cursor-not-allowed'
                                        >
                                            <EnvelopeIcon className='size-5' />
                                            {isSubmitting
                                                ? rawT('natural-booking.submitting')
                                                : rawT('natural-booking.submit')}
                                        </button>
                                    </fetcher.Form>
                                </div>
                            )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}

function Field({ label, error, required, children }: {
    label: string;
    error?: string;
    required?: boolean;
    children: React.ReactNode;
}) {
    return (
        <label className='block text-sm font-bold'>
            <span className='mb-2 block'>
                {label}
                {required && <span className='ps-0.5 text-[var(--core-error,#dc2626)]'>*</span>}
            </span>
            {children}
            {error && <span className='mt-1 block text-xs text-[var(--core-error,#dc2626)]'>{error}</span>}
        </label>
    );
}
